package fr.frux.shared.data.mapper

import fr.frux.shared.domain.model.Notification
import fr.frux.shared.domain.model.NotificationKind
import fr.frux.shared.domain.model.NotificationPage
import fr.frux.shared.domain.model.NotificationPreferences
import fr.frux.shared.domain.model.NotificationSubjectType
import fr.frux.shared.dto.NotificationDto
import fr.frux.shared.dto.NotificationKindDto
import fr.frux.shared.dto.NotificationListResponse
import fr.frux.shared.dto.NotificationPreferencesDto
import fr.frux.shared.dto.NotificationSubjectTypeDto

/**
 * Traduction transport ↔ domaine (#109), même patron que [HarvestDto.toDomain].
 */
fun NotificationDto.toDomain(): Notification =
    Notification(
        id = id,
        kind = kind.toDomain(),
        subjectType = subjectType.toDomain(),
        subjectId = subjectId,
        actorUserId = actorUserId,
        payload = payload,
        createdAt = createdAt.toEpochMilliseconds(),
        readAt = readAt?.toEpochMilliseconds(),
    )

// Table de correspondance enum à enum, une branche par kind : la complexité est celle de la liste.
@Suppress("CyclomaticComplexMethod")
fun NotificationKindDto.toDomain(): NotificationKind =
    when (this) {
        NotificationKindDto.HARVEST_JOINED -> NotificationKind.HARVEST_JOINED
        NotificationKindDto.HARVEST_CANCELLED -> NotificationKind.HARVEST_CANCELLED
        NotificationKindDto.HARVEST_STARTED -> NotificationKind.HARVEST_STARTED
        NotificationKindDto.TREE_DISPUTED -> NotificationKind.TREE_DISPUTED
        NotificationKindDto.REPORT_RESOLVED -> NotificationKind.REPORT_RESOLVED
        NotificationKindDto.MATURITY_DIGEST -> NotificationKind.MATURITY_DIGEST
        NotificationKindDto.SESSION_REVOKED -> NotificationKind.SESSION_REVOKED
        NotificationKindDto.SPECIES_SUGGESTION_ACCEPTED -> NotificationKind.SPECIES_SUGGESTION_ACCEPTED
        NotificationKindDto.SPECIES_SUGGESTION_DISMISSED -> NotificationKind.SPECIES_SUGGESTION_DISMISSED
        NotificationKindDto.TREE_SPECIES_CORRECTED -> NotificationKind.TREE_SPECIES_CORRECTED
        NotificationKindDto.CONTENT_MODERATED -> NotificationKind.CONTENT_MODERATED
        NotificationKindDto.HARVEST_LEFT -> NotificationKind.HARVEST_LEFT
        NotificationKindDto.HARVEST_FULL -> NotificationKind.HARVEST_FULL
        NotificationKindDto.HARVEST_COMPLETED -> NotificationKind.HARVEST_COMPLETED
        NotificationKindDto.FEEDBACK_HANDLED -> NotificationKind.FEEDBACK_HANDLED
        NotificationKindDto.TREE_ACTIVITY -> NotificationKind.TREE_ACTIVITY
        NotificationKindDto.FAVORITE_TREE_CHANGED -> NotificationKind.FAVORITE_TREE_CHANGED
        NotificationKindDto.HARVEST_REMINDER -> NotificationKind.HARVEST_REMINDER
        NotificationKindDto.PREMIUM_EXPIRING -> NotificationKind.PREMIUM_EXPIRING
        NotificationKindDto.PREMIUM_EXPIRED -> NotificationKind.PREMIUM_EXPIRED
        NotificationKindDto.UNKNOWN -> NotificationKind.UNKNOWN
    }

fun NotificationSubjectTypeDto.toDomain(): NotificationSubjectType =
    when (this) {
        NotificationSubjectTypeDto.HARVEST -> NotificationSubjectType.HARVEST
        NotificationSubjectTypeDto.TREE -> NotificationSubjectType.TREE
        NotificationSubjectTypeDto.DIGEST -> NotificationSubjectType.DIGEST
        NotificationSubjectTypeDto.ACCOUNT -> NotificationSubjectType.ACCOUNT
        NotificationSubjectTypeDto.SPECIES_SUGGESTION -> NotificationSubjectType.SPECIES_SUGGESTION
        NotificationSubjectTypeDto.FEEDBACK -> NotificationSubjectType.FEEDBACK
        NotificationSubjectTypeDto.USER_REPORT -> NotificationSubjectType.USER_REPORT
        NotificationSubjectTypeDto.UNKNOWN -> NotificationSubjectType.UNKNOWN
    }

/**
 * [UNKNOWN] (#253/#157, résilience aux kinds futurs) est retiré de la liste rendue : un kind non
 * reconnu de ce client n'a ni libellé ni navigation possibles, l'afficher serait une ligne muette.
 * [unreadCount] est ajusté du même nombre pour la page courante. L'envelope serveur compte tous
 * les non-lus, y compris ceux qu'on vient de filtrer ; approximatif au-delà de la page chargée (pas
 * de second aller-retour dédié), mais le backend n'écrit jamais [NotificationKindDto.UNKNOWN]
 * aujourd'hui (CHECK V29) : ce correctif ne joue qu'au jour où un nouveau kind existera et que ce
 * client n'aura pas encore été mis à jour.
 */
fun NotificationListResponse.toDomain(): NotificationPage {
    val visible = notifications.filter { it.kind != NotificationKindDto.UNKNOWN }
    val droppedUnread = notifications.count { it.kind == NotificationKindDto.UNKNOWN && it.readAt == null }
    return NotificationPage(
        notifications = visible.map { it.toDomain() },
        unreadCount = (unreadCount - droppedUnread).coerceAtLeast(0),
        nextCursor = nextCursor,
    )
}

fun NotificationPreferencesDto.toDomain(): NotificationPreferences =
    NotificationPreferences(
        inAppEnabled = inAppEnabled,
        emailEnabled = emailEnabled,
        pushEnabled = pushEnabled,
        harvestEvents = harvestEvents,
        treeEvents = treeEvents,
        maturityAlerts = maturityAlerts,
        maturityAlertsLocked = maturityAlertsLocked,
    )
